
import React, { useState, useEffect } from 'react';
import Typography from '../../components/Typography/Typography';
import Button from '../../components/Button/Button';
import AzurePagination from '../../components/Pagination/AzurePagination';
import DataTable, { dataTableRowClass, dataTableTdClass, dataTableThClass, searchInputClass, TABLE_HEIGHT } from '../../components/DataTable/DataTable';
import { FiUsers } from 'react-icons/fi';
import ASSETS from '../../assets';
import AddDomain from '../../dialogs/AddDomain/AddDomain';
import DomainAction from '../../dialogs/DomainAction/DomainAction';
import DeleteDomain from '../../dialogs/DeleteDomain/DeleteDomain';
import VerifyDomain from '../../dialogs/VerifyDomain/VerifyDomain';
import { authAPI, DomainMember, AzureDomainsResponse, SearchDomainResponse, AzureDomainsRequest } from '../../services/api';
import toast from 'react-hot-toast';
import { ClipLoader } from 'react-spinners';

interface Domain {
  id: string;
  domain: string;
  createdAt: string;
  status: 'verified' | 'unverified';
}

const Domain = () => {
  const [isAddUserModalOpen, setIsAddUserModalOpen] = useState(false);
  const [isDomainActionModalOpen, setIsDomainActionModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isVerifyModalOpen, setIsVerifyModalOpen] = useState(false);
  const [selectedDomain, setSelectedDomain] = useState<Domain | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState('');
  const [domains, setDomains] = useState<Domain[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchResults, setSearchResults] = useState<SearchDomainResponse[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const [showSearchResults, setShowSearchResults] = useState(false);
  const [searchError, setSearchError] = useState<string | null>(null);
  const [isValidDomain, setIsValidDomain] = useState(false);
  const [skipToken, setSkipToken] = useState<string>('');
  const [total, setTotal] = useState(0);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const itemsPerPage = 10;

  // Fetch domains from API
  const fetchDomains = async (page: number = 1, search: string = '', currentSkipToken: string = '') => {
    try {
      setLoading(true);
      setError(null);
      
      if (search && search.trim()) {
        // Search API call
        const searchResponse: SearchDomainResponse[] = await authAPI.searchDomain({ input: search.trim() });
        
        // Transform search results to Domain format
        const transformedDomains: Domain[] = searchResponse.map((result) => ({
          id: result.id,
          domain: result.displayName,
          createdAt: new Date().toISOString(),
          status: 'unverified' as const,
        }));
        
        setDomains(transformedDomains);
        setTotal(transformedDomains.length);
        setSkipToken('');
      } else {
        // Regular pagination API call
        const requestPayload: AzureDomainsRequest = { pageSize: itemsPerPage };
        if (currentSkipToken && currentSkipToken.trim()) {
          requestPayload.skipToken = currentSkipToken;
        }
        
        const response: AzureDomainsResponse = await authAPI.getDomainStatuses(requestPayload);
      
        // Transform API response to match our Domain interface
        const transformedDomains: Domain[] = response.members.map((member: DomainMember) => ({
          id: member.id,
          domain: member.displayName,
          createdAt: member.createdAt,
          status: 'unverified' as const, // Default to unverified, can be updated based on verification logic
        }));
        
        setDomains(transformedDomains);
        setTotal(response.total);
        setSkipToken(response.skipToken || '');
      }
    } catch (err: any) {
      let errorMessage = 'Failed to fetch domains. Please try again.';
      
      if (err.response?.data?.message) {
        errorMessage = err.response.data.message;
      } else if (err.response?.status === 401) {
        errorMessage = 'Unauthorized. Please login again.';
      } else if (err.response?.status === 500) {
        errorMessage = 'Server error. Please try again later.';
      } else if (err.message) {
        errorMessage = err.message;
      }
      
      setError(errorMessage);
      toast.error(errorMessage);
      console.error('Error fetching domains:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDomains(1, searchTerm, '');
  }, []);

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
    if (page === 1) {
      // Go to page 1 - reset and fetch first page
      setSkipToken('');
      fetchDomains(1, searchTerm, '');
    } else if (page > currentPage && skipToken) {
      // Load next page using skipToken
      fetchDomains(page, searchTerm, skipToken);
    } else if (page < currentPage) {
      // For previous pages, we need to reset and fetch from beginning
      // This is a limitation of skipToken pagination - we can't go back easily
      setSkipToken('');
      fetchDomains(1, searchTerm, '');
    }
  };

  // Validate domain name format
  const validateDomainName = (domain: string): boolean => {
    const domainRegex = /^[a-zA-Z0-9][a-zA-Z0-9-]{0,61}[a-zA-Z0-9]?\.([a-zA-Z]{2,}|[a-zA-Z]{2,}\.[a-zA-Z]{2,})$/;
    return domainRegex.test(domain);
  };

  // Search domains from backend
  const searchDomains = async () => {
    if (!searchTerm.trim()) {
      setSearchError('Please enter a domain name');
      return;
    }

    if (!validateDomainName(searchTerm.trim())) {
      setSearchError('Please enter a complete domain name (e.g., example.com)');
      return;
    }

    try {
      setIsSearching(true);
      setSearchError(null);
      setCurrentPage(1);
      setSkipToken('');
      await fetchDomains(1, searchTerm, '');
      setShowSearchResults(true);
    } catch (err: any) {
      console.error('Error searching domains:', err);
      setSearchError('Failed to search domains. Please try again.');
      setSearchResults([]);
      setShowSearchResults(false);
    } finally {
      setIsSearching(false);
    }
  };

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setSearchTerm(value);
    setCurrentPage(1);
    setSearchError(null);
    
    // Check if domain is valid
    const isValid = validateDomainName(value);
    setIsValidDomain(isValid);
    
    // Clear search results when typing
    if (showSearchResults) {
      setShowSearchResults(false);
      setSearchResults([]);
    }
  };

  const handleClearSearch = () => {
    setSearchTerm('');
    setSearchResults([]);
    setShowSearchResults(false);
    setSearchError(null);
    setIsValidDomain(false);
    setCurrentPage(1);
    setSkipToken('');
    // Reset to original domains
    fetchDomains(1, '', '');
  };

  const handleSearchIconClick = () => {
    if (isValidDomain) {
      searchDomains();
    } else {
      setSearchError('Please enter a complete domain name (e.g., example.com)');
    }
  };

  // Refresh domains data
  const handleRefresh = async () => {
    try {
      setIsRefreshing(true);
      setCurrentPage(1);
      setSkipToken('');
      setSearchTerm('');
      setSearchResults([]);
      setShowSearchResults(false);
      setSearchError(null);
      setIsValidDomain(false);
      await fetchDomains(1, '', '');
      toast.success('Domains refreshed successfully!');
    } catch (err: any) {
      console.error('Error refreshing domains:', err);
      toast.error('Failed to refresh domains. Please try again.');
    } finally {
      setIsRefreshing(false);
    }
  };


  const handleDeleteDomain = (domain: Domain) => {
    setSelectedDomain(domain);
    setIsDeleteModalOpen(true);
  };



  const handleAddDomain = async (domainName: string) => {
    try {
      const response = await authAPI.addCustomDomain({ domain: domainName });
      toast.success(response.message || 'Domain added successfully!');
      
      // Close modal immediately
      setIsAddUserModalOpen(false);
      
      // First API call immediately
      await fetchDomains(1, searchTerm, '');
      
      // Second API call after 2 seconds
      setTimeout(async () => {
        await fetchDomains(1, searchTerm, '');
      }, 2000);
      
    } catch (err: any) {
      let errorMessage = 'Failed to add domain. Please try again.';
      
      if (err.response?.data?.message) {
        errorMessage = err.response.data.message;
      } else if (err.response?.status === 400) {
        errorMessage = 'Invalid domain format. Please check and try again.';
      } else if (err.response?.status === 409) {
        errorMessage = 'This domain already exists.';
      } else if (err.response?.status === 500) {
        errorMessage = 'Server error. Please try again later.';
      } else if (err.message) {
        errorMessage = err.message;
      }
      
      toast.error(errorMessage);
      console.error('Error adding domain:', err);
    }
  };

  const handleConfirmDeleteDomain = async () => {
    if (!selectedDomain) return;
    try {
      const response = await authAPI.deleteCustomDomain({ domain: selectedDomain.domain });
      toast.success(response.message || 'Domain deleted successfully!');
      await fetchDomains(1, searchTerm, ''); // Refresh the list
      setIsDeleteModalOpen(false);
      setSelectedDomain(null);
    } catch (err: any) {
      let errorMessage = 'Failed to delete domain. Please try again.';
      
      if (err.response?.data?.message) {
        errorMessage = err.response.data.message;
      } else if (err.response?.status === 404) {
        errorMessage = 'Domain not found.';
      } else if (err.response?.status === 400) {
        errorMessage = 'Invalid request. Please try again.';
      } else if (err.response?.status === 500) {
        errorMessage = 'Server error. Please try again later.';
      } else if (err.message) {
        errorMessage = err.message;
      }
      
      toast.error(errorMessage);
      console.error('Error deleting domain:', err);
    }
  };



  const getStatusBadge = (status: string) => {
    if (status === 'whitelisted') {
      return (
        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800 border border-green-200">
          Whitelisted
        </span>
      );
    } else {
      return (
        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-orange-100 text-orange-800 border border-orange-200">
          Unverified
        </span>
      );
    }
  };

  return (
    <div className='flex-grow bg-white  rounded-[15px] flex flex-col'>

  
      <div className='  rounded-lg'>
        {/* Header Section */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-6 gap-4">
          <div className="flex items-center gap-3">
            <Typography variant="h2" className="font-bold title text-xl sm:text-2xl lg:text-2xl">
              Domain
            </Typography>
            
            {/* Refresh Button */}
            <div className="relative group">
              <button
                onClick={handleRefresh}
                disabled={isRefreshing}
                className={`p-2 rounded-full transition-all duration-200 ${
                  isRefreshing 
                    ? 'bg-blue-100 cursor-not-allowed' 
                    : 'bg-gray-100 hover:bg-blue-100 hover:text-blue-600'
                }`}
                title="Refresh domains list"
              >
                <svg 
                  className={`w-4 h-4 transition-transform duration-200 ${
                    isRefreshing ? 'animate-spin text-blue-600' : 'text-gray-600'
                  }`} 
                  fill="none" 
                  stroke="currentColor" 
                  viewBox="0 0 24 24"
                >
                  <path 
                    strokeLinecap="round" 
                    strokeLinejoin="round" 
                    strokeWidth={2} 
                    d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" 
                  />
                </svg>
              </button>
              
              {/* Tooltip */}
              <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-3 py-2 bg-gray-800 text-white text-sm rounded-lg opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none whitespace-nowrap z-50">
                Azure takes time to load the data
                <div className="absolute top-full left-1/2 transform -translate-x-1/2 w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent border-t-gray-800"></div>
              </div>
            </div>
          </div>

          <div className="flex flex-col sm:flex-row gap-3 w-full sm:w-auto">
            {/* Search Bar */}
            <div className="relative flex-1 sm:flex-none sm:w-80">
              <input
                type="text"
                placeholder="Enter complete domain name (e.g., example.com)"
                value={searchTerm}
                onChange={handleSearchChange}
                className={`${searchInputClass} ${
                  searchError ? 'border-red-400 focus:ring-red-200' : isValidDomain ? 'border-green-400 focus:ring-green-200' : ''
                }`}
              />
              <div className="absolute inset-y-0 right-0 flex items-center pr-3 gap-2">
                {/* Loading Spinner */}
                {isSearching && (
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600"></div>
                )}
                {/* Clear Button */}
                {searchTerm && !isSearching && (
                  <button
                    onClick={handleClearSearch}
                    className="p-1 hover:bg-gray-100 rounded-full transition-colors duration-200"
                    title="Clear search"
                  >
                    <img src={ASSETS.cross} alt="Clear" className="w-3 h-3 text-gray-400 hover:text-gray-600" />
                  </button>
                )}
                {/* Search Icon */}
                {ASSETS.search && !isSearching && (
                  <button
                    onClick={handleSearchIconClick}
                    className="p-1 hover:bg-gray-100 rounded-full transition-colors duration-200"
                    title="Search domain"
                  >
                    <img 
                      src={ASSETS.search} 
                      alt="Search" 
                      className={`w-4 h-4 transition-colors duration-200 ${
                        isValidDomain ? 'text-blue-600' : 'text-gray-400'
                      }`} 
                    />
                  </button>
                )}
              </div>
              
              {/* Search Error Message */}
              {searchError && (
                <div className="absolute top-full left-0 right-0 mt-1 bg-red-50 border border-red-200 rounded-lg shadow-lg z-50 p-3">
                  <div className="text-red-600 text-sm">{searchError}</div>
                </div>
              )}
            </div>
            
            {/* Add User Button */}
            <Button
              label="Add Domain"
              variant="primary"
              onClick={() => setIsAddUserModalOpen(true)}
              className="w-full sm:w-auto flex items-center gap-1 !h-[38px]"
              leftIcon={ASSETS.add}
            />
          </div>
        </div>

        {/* Table Container */}
        <DataTable
          footer={
            !loading && !error && domains.length > 0 ? (
              <AzurePagination
                currentPage={currentPage}
                onPageChange={handlePageChange}
                totalItems={total}
                itemsPerPage={itemsPerPage}
                hasNextPage={Boolean(skipToken && skipToken.trim() !== '')}
                isLoading={loading}
                embedded
              />
            ) : undefined
          }
        >
            <table className="w-full border-collapse">
              <thead className="sticky top-0 z-10 shadow-[0_1px_0_0_rgba(226,232,240,1)]">
                <tr>
                  <th className={`${dataTableThClass} text-center min-w-[40px]`}>
                    Sr.
                  </th>
                  <th className={`${dataTableThClass} text-left min-w-[200px]`}>
                    Domain
                  </th>
                  <th className={`${dataTableThClass} text-center min-w-[120px]`}>
                    Status
                  </th>
                  <th className={`${dataTableThClass} text-center min-w-[100px]`}>
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan={4} className="text-center align-middle" style={{ height: TABLE_HEIGHT - 48 }}>
                      <div className="flex flex-col items-center justify-center gap-3 h-full">
                        <ClipLoader size={40} color="#3B82F6" />
                      </div>
                    </td>
                  </tr>
                ) : error ? (
                  <tr>
                    <td colSpan={4} className="text-center align-middle" style={{ height: TABLE_HEIGHT - 48 }}>
                      <div className="flex flex-col items-center justify-center gap-3 h-full">
                        <div className="p-3 bg-red-100 rounded-full">
                          <img src={ASSETS.cross} alt="Error" className="w-8 h-8 text-red-400" />
                        </div>
                        <Typography variant="h3" className="text-red-500 font-medium">
                          Error Loading Domains
                        </Typography>
                        <Typography variant="body1" className="text-red-400">
                          {error}
                        </Typography>
                        <button
                          onClick={() => fetchDomains(1, searchTerm, '')}
                          className="mt-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-medium transition-colors duration-200"
                        >
                          Try Again
                        </button>
                      </div>
                    </td>
                  </tr>
                ) : domains.length > 0 ? (
                  domains.map((domain, index) => (
                    <tr key={domain.id} className={dataTableRowClass}>
                      <td className={`${dataTableTdClass} text-center min-w-[40px] font-medium text-slate-500`}>
                        {index + 1}
                      </td>
                      <td className={`${dataTableTdClass} min-w-[200px]`}>
                        <a 
                          href={`https://${domain.domain}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-blue-600 underline cursor-pointer hover:text-blue-800 transition-colors duration-200"
                          title={`Open ${domain.domain} in new tab`}
                        >
                          {domain.domain}
                        </a>
                      </td>
                      <td className={`${dataTableTdClass} text-center min-w-[120px]`}>
                        {getStatusBadge("whitelisted")}
                      </td>
                      <td className={`${dataTableTdClass} text-center min-w-[120px]`}>
                        <div className="flex items-center justify-center gap-2">
                          {/* Show different icons based on domain status */}
                          {domain.status === 'verified' ? (
                            // For verified domains: only delete button
                            <button
                              onClick={() => handleDeleteDomain(domain)}
                              className="hover:bg-red-100 rounded-full p-1 transition-colors duration-200"
                              title="Delete domain"
                            >
                              <img src={ASSETS.delete} alt="Delete" className="w-4 h-4" />
                            </button>
                          ) : (
                            // For unverified domains: edit and delete buttons
                            <>
                             
                              <button
                                onClick={() => handleDeleteDomain(domain)}
                                className="hover:bg-red-100 rounded-full p-1 transition-colors duration-200"
                                title="Delete domain"
                              >
                                <img src={ASSETS.delete} alt="Delete" className="w-4 h-4" />
                              </button>
                            </>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={4} className="text-center align-middle" style={{ height: TABLE_HEIGHT - 48 }}>
                      <div className="flex flex-col items-center justify-center gap-3 h-full">
                        <div className="p-3 bg-gray-100 rounded-full">
                          {FiUsers({ className: "w-8 h-8 text-gray-400" })}
                        </div>
                        <Typography variant="h3" className="text-gray-500 font-medium">
                          {searchTerm ? 'No domains found' : 'No domains available'}
                        </Typography>
                        <Typography variant="body1" className="text-gray-400">
                          {searchTerm 
                            ? `No domains match "${searchTerm}". Try adjusting your search terms.`
                            : 'No domains have been added yet. Click "Add Domain" to get started.'
                          }
                        </Typography>
                        {searchTerm && (
                          <button
                            onClick={handleClearSearch}
                            className="mt-2 px-4 py-2 text-blue-600 hover:text-blue-700 font-medium transition-colors duration-200"
                          >
                            Clear search
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
        </DataTable>
      </div>

      {/* Add Domain Modal */}
      <AddDomain
        open={isAddUserModalOpen}
        setOpen={setIsAddUserModalOpen}
        onAddDomain={handleAddDomain}
      />

  

      {/* Delete Confirmation Modal */}
      <DeleteDomain
        open={isDeleteModalOpen}
        setOpen={setIsDeleteModalOpen}
        domain={selectedDomain?.domain}
        onConfirmDelete={handleConfirmDeleteDomain}
      />

    </div>
  );
};

export default Domain;
